import type { ReactNode } from 'react';

import { CloseDeleteIcon } from '@/styles/Icons/CloseDelete';
import { classNames } from '@/utils/helpers/classNames';
import { formatDateTime, formatTimeAgo } from '@/utils/helpers/formatTimeDate';

import css from './EntryDraftNotice.module.css';
import { NoticeLinkButton } from './EntryDraftNotice.style';
import { ENTRY_DRAFT_MESSAGES } from './entryDraftMessages';
import type { EntryDraftNoticeState } from './types';

interface NoticeProps {
  text: string;
  /** A line of its own under the text, e.g. "(3 дня назад)". */
  subtext?: string;
  /** Shown on hover over the subtext, e.g. the exact date behind "3 дня назад". */
  subtextTitle?: string;
  actions: ReactNode;
  onClose: () => void;
}

function Notice({ text, subtext, subtextTitle, actions, onClose }: NoticeProps) {
  return (
    // the root only defines the container the layout adapts to
    <div className={css.root}>
      <div
        // the second class is a stable hook for custom blog styles
        className={classNames({ [css.notice]: true, 'entry-draft-notice': true })}
        role="status"
      >
        <span className={css.text}>
          {text}
          {subtext && (
            <span className={css.subtext} title={subtextTitle}>
              {subtext}
            </span>
          )}
        </span>
        <span className={css.actions}>{actions}</span>
        <button
          type="button"
          className={css.close}
          onClick={onClose}
          aria-label={ENTRY_DRAFT_MESSAGES.close}
          title={ENTRY_DRAFT_MESSAGES.close}
        >
          <CloseDeleteIcon className={css.closeIcon} />
        </button>
      </div>
    </div>
  );
}

/**
 * Tells the user the form shows their last edit and lets them reset it to the
 * server version, or bring the edit back. Meant to sit inside the editor, see
 * the `notice` prop of Editor.
 */
export function EntryDraftNotice({
  notice,
  draftSavedAt,
  restoreOriginal,
  restoreDraft,
  dismissNotice
}: EntryDraftNoticeState) {
  if (notice === 'draft-shown') {
    return (
      <Notice
        text={ENTRY_DRAFT_MESSAGES.draftAvailable}
        subtext={
          draftSavedAt
            ? ENTRY_DRAFT_MESSAGES.draftSavedAgo(formatTimeAgo(draftSavedAt))
            : undefined
        }
        subtextTitle={draftSavedAt ? formatDateTime(draftSavedAt) : undefined}
        actions={
          <NoticeLinkButton type="button" onClick={restoreOriginal}>
            {ENTRY_DRAFT_MESSAGES.restoreOriginal}
          </NoticeLinkButton>
        }
        onClose={dismissNotice}
      />
    );
  }

  if (notice === 'original-restored') {
    return (
      <Notice
        text={ENTRY_DRAFT_MESSAGES.originalRestored}
        actions={
          <NoticeLinkButton type="button" onClick={restoreDraft}>
            {ENTRY_DRAFT_MESSAGES.undoRestore}
          </NoticeLinkButton>
        }
        onClose={dismissNotice}
      />
    );
  }

  return null;
}
